import { service } from '@ember/service';
import { isTesting } from '@embroider/macros';
import type IntlService from 'ember-intl/services/intl';
import type AditoolsAnalytics from '@ebiz/ember-aditools-analytics/services/aditools-analytics';
import type AditoolsSite from '@ebiz/ember-aditools-site/services/aditools-site';
import type RouterService from '@ember/routing/router-service';
import type { SimDDSSiteConfigData } from 'simdds';
import '@oddbird/popover-polyfill';

import type CdpService from '../services/cdp';
import type StateService from '../services/state';
import type CircuitService from '../services/circuit';
import type FilterService from '../services/filter';
import type LoggingService from '../services/logger';
import type { JsonProduct } from '../models/product';
import type { ProductRow } from '../services/state';
type Transition = ReturnType<RouterService['transitionTo']>;
type Resolved<P> = P extends Promise<infer T> ? T : P;
export type IndexRouteModel = Resolved<ReturnType<IndexRoute['model']>>;

import Route from '@ember/routing/route';
import IndexController from '../controllers/index';
import ENV from 'simdds/config/environment';
import { BuildProductRows } from '../models/product';

const LOCALES = { 'en-us': 'en', 'zh-cn': 'cn', 'ja-jp': 'jp', 'ru-ru': 'ru' };

type ValidLanguage = 'en-us' | 'zh-cn' | 'ja-jp' | 'ru-ru';
/**
 * Handles the route for index.
 *
 * When the app starts, it goes via the route.
 * In this case, starts with model(). It gets the data from the data folder.
 * The model at this point is the json data. However, we still need to do
 * something about this data. This is just a part of a bigger model. So when
 * the setupController runs, we manually create an IndexModel and set the value of
 * indexModel.products to the json data. The created IndexModel is then set as
 * the model of the controller.
 *
 * @module
 * @augments ember/Route
 * @public
 */
export default class IndexRoute extends Route {
  @service declare intl: IntlService;
  @service('aditools-analytics') declare analytics: AditoolsAnalytics;
  @service('aditools-site') declare siteconfig: AditoolsSite & SimDDSSiteConfigData;
  @service declare cdp: CdpService;
  @service declare state: StateService;
  @service declare circuit: CircuitService;
  @service declare filter: FilterService;
  @service declare logger: LoggingService;

  /**
   * Provides the initial model from products.json
   * returns a promise which is then resolved by the framework
   * before going to the setupController method.
   * @param {hash} params
   * @returns {Promise<TransientModel>}
   */
  model(params: { lang?: string }) {
    const configURL = ENV.rootURL.endsWith('/') ? ENV.rootURL + 'siteconfig.json' : ENV.rootURL + '/siteconfig.json';
    /*const docService = getOwner(this).lookup('service:-document');*/
    // This little song and dance is because document isn't defined in fastboot, but docService is
    const htmlElement = /*docService ? docService.documentElement :*/ document
      ? document.querySelector('html')
      : undefined;
    const candidateLanguage = htmlElement?.getAttribute('lang');
    const languageInvalid = !candidateLanguage || !Object.keys(LOCALES).includes(candidateLanguage);
    const fullLanguage: ValidLanguage = languageInvalid ? 'en-us' : (candidateLanguage as ValidLanguage);
    const defaultLanguage = params.lang ? params.lang : LOCALES[fullLanguage];
    return this.siteconfig
      .start(configURL, ENV.rootURL, defaultLanguage, this.intl.locales.concat('ru-ru'), this.logger)
      .then((/*site*/) => {
        if (this.siteconfig.FullLocale === 'ru-ru') {
          this.intl.setLocale('en-us');
        } else {
          this.intl.setLocale(this.siteconfig.FullLocale);
        }
        this.analytics.start(
          ENV.toolname,
          this.siteconfig.AnalyticsUrl,
          false,
          isTesting(),
          ENV.environment === 'development',
          this.logger,
        );
        return this.cdp.getToolData().then(
          (result) => {
            result.products.sort((item1: JsonProduct, item2: JsonProduct) => {
              if (item1.id > item2.id) return 1;
              if (item1.id < item2.id) return -1;
              return 0;
            });
            const productData = BuildProductRows(result.products);
            const referrerPart = this.getReferrerProductIfAny(productData);
            this.state.start(productData, referrerPart, params);
            return { products: productData, referrerPart: referrerPart, parameters: params };
          },
          (error: Error) => {
            console.log(`Error from getToolData: ${error.toString()}`);
          },
        );
      });
  }

  /**
   *  setupController is an ember lifecycle hook.
   *  This is executed after the call for the model.
   *  The model function gets the json from the data folder then creates an
   *  instance of the Index model. The json data is then set to the products
   *  variable.
   * @param {IndexController} controller
   * @param {TransientModel} model
   */
  setupController(controller: IndexController, model: IndexRouteModel, transition: Transition) {
    super.setupController(controller, model, transition);
    controller.setupStudioLinks();
    if (model) {
      // Promise.resolve can't error out, so...
      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      Promise.resolve().then(() => {
        this.circuit.updateParams();
      });
    } else {
      console.error('No loaded products');
    }
  }

  /**
   * Get the part number specified by the referrer if there is one
   * @function
   * @param {array | product-row} products
   * @returns {string | undefined }
   * @private
   */
  getReferrerProductIfAny(products: ProductRow[]) {
    const referrer =
      document.referrer.toLowerCase() ||
      (document.querySelector('meta[name="ref"]') &&
        document.querySelector('meta[name="ref"]')?.getAttribute('content')?.toLowerCase());
    if (!referrer) return;
    const referrerUrl = new URL(referrer);
    const referredFromMe =
      referrerUrl.protocol === document.location.protocol &&
      referrerUrl.host === document.location.host &&
      referrerUrl.port === document.location.port &&
      referrerUrl.pathname === document.location.pathname;
    if (referredFromMe) return;
    console.log(`Referring from ${referrer}`);
    for (let i = 0; i < products.length; i++) {
      const product = products[i] as ProductRow;
      if (referrer.indexOf(product.name.toLowerCase()) !== -1) {
        console.log(`Referred ${product.name} from ${referrer}`);
        return product.name;
      }
    }
  }
}
